import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';

import { buildDisableConfirmBody } from './bulkConfirmCopy';

export interface BulkActionBarProps {
  /** Pair count the "All ON / All OFF" actions actually flip — with a
   *  group filter that is the WHOLE group (server-side scope), not just
   *  the search-narrowed visible rows. Feeds the "all" confirm copy. */
  totalCount: number;
  /** Currently-selected row count. */
  selectedCount: number;
  /** True while a bulk request is in flight — disables every control. */
  busy?: boolean;
  /** Noun shown in the bar + dialog. Defaults to "market data". */
  noun?: string;
  /** Display name of the active group filter — makes the "all" confirm
   *  copy explicit ("ALL pairs in {group}"). Null/absent = All groups. */
  groupLabel?: string | null;
  /** Extra sentence appended to the disable-confirm dialog body. */
  disableWarning?: string;
  onEnableSelected: () => void;
  onDisableSelected: () => void;
  onClearSelection: () => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
}

/**
 * Compact toolbar for bulk market-data ON/OFF, shared by the Opening and
 * Unwinding pages. It is CONTEXTUAL — one mode at a time:
 *  - no rows selected → master "All ON / All OFF" (server-side scope:'all').
 *  - rows selected   → "Enable / Disable" acting on just that subset
 *    (scope:'ids'), plus Clear.
 * Every "disable" path routes through a confirmation dialog — enabling never
 * needs one (arming a pair costs nothing, symmetric with the per-row switch).
 */
export function BulkActionBar({
  totalCount,
  selectedCount,
  busy = false,
  noun = 'market data',
  groupLabel,
  disableWarning,
  onEnableSelected,
  onDisableSelected,
  onClearSelection,
  onEnableAll,
  onDisableAll
}: BulkActionBarProps) {
  const [confirm, setConfirm] = useState<'selected' | 'all' | null>(null);

  const btnSx = { fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 } as const;
  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };

  const hasSelection = selectedCount > 0;

  const confirmBody = buildDisableConfirmBody({
    mode: confirm === 'all' ? 'all' : 'selected',
    noun,
    totalCount,
    selectedCount,
    groupLabel
  });

  const runConfirmed = () => {
    if (confirm === 'all') onDisableAll();
    else if (confirm === 'selected') onDisableSelected();
    setConfirm(null);
  };

  return (
    <>
      {busy ? (
        /* Busy mode — a prominent, unambiguous status that spans the whole
         * effect (PATCH + the WS settle the caller waits on). Replaces the
         * button row entirely so the operator can't fire a second flip and
         * always sees that the action registered, even on a sub-second
         * bulk write. */
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}
        >
          <CircularProgress size={13} thickness={5} />
          <Typography sx={{ ...labelSx, color: 'grey.300' }}>
            Applying — {noun}…
          </Typography>
        </Box>
      ) : hasSelection ? (
        /* Selection mode — acts on the checked subset only (scope:'ids'). */
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ ...labelSx, color: 'grey.300' }}>
            {selectedCount} selected
          </Typography>
          <Button
            size="small"
            color="success"
            variant="outlined"
            sx={btnSx}
            disabled={busy}
            onClick={onEnableSelected}
          >
            Enable
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            sx={btnSx}
            disabled={busy}
            onClick={() => setConfirm('selected')}
          >
            Disable
          </Button>
          <Button
            size="small"
            sx={{ ...btnSx, color: 'grey.500' }}
            disabled={busy}
            onClick={onClearSelection}
          >
            Clear
          </Button>
        </Box>
      ) : (
        /* Master mode — flips every eligible pair server-side (scope:'all'). */
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={labelSx}>All {noun}</Typography>
          <Button
            size="small"
            color="success"
            sx={btnSx}
            disabled={busy}
            onClick={onEnableAll}
          >
            All ON
          </Button>
          <Button
            size="small"
            color="error"
            sx={btnSx}
            disabled={busy}
            onClick={() => setConfirm('all')}
          >
            All OFF
          </Button>
        </Box>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>
          Disable {noun}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            {confirmBody}
            {disableWarning ? ` ${disableWarning}` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" onClick={runConfirmed} autoFocus>
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
